import { Manifest, TimeStep } from '../types';
import { cleanHour } from '../config';

export interface FrameMeta {
  dateStr: string;
  hourStr: string;
  timestamp: number;
  key?: string;
  raw?: string;
}

export interface BracketingFrames {
  frame0: FrameMeta;
  frame1: FrameMeta;
  blend: number; // 0.0 to 1.0
  intervalHours: number;
}

export interface ProductBoundsInfo {
  product: string;
  minTime: Date;
  maxTime: Date;
  frames: FrameMeta[];
  color: string;
  icon: string;
  label: string;
  type?: 'forecast' | 'observation';
}

export interface ProductMetaInfo {
  color: string;
  icon: string;
  label: string;
  type: 'forecast' | 'observation';
}

export const PRODUCT_META: Record<string, ProductMetaInfo> = {
  live_weather: { color: 'var(--color-liveweather, #00d4ff)', icon: '📡', label: 'Live Weather (Radar/Sat)', type: 'observation' },
  streamflow: { color: '#06b6d4', icon: '🌊', label: 'Streamflow Observations', type: 'observation' },
  wildfires: { color: '#ef4444', icon: '🔥', label: 'Wildfires & Hotspots', type: 'observation' },
  fire_restrictions: { color: '#f59e0b', icon: '🔥🚫', label: 'Fire Restrictions', type: 'observation' },
  avalanche: { color: '#f7941e', icon: '🏔️⚠️', label: 'Avalanche Danger', type: 'observation' },
  sunrise: { color: 'var(--color-sunrise, #f97316)', icon: '☀️', label: 'Sunrise/Sunset Forecast', type: 'forecast' },
  alpenglow: { color: 'var(--color-sunrise, #ea580c)', icon: '🌄', label: 'Alpenglow Forecast', type: 'forecast' },
  aurora: { color: '#10b981', icon: '🌌', label: 'Aurora Forecast', type: 'forecast' },
  darkskies: { color: 'var(--color-darkskies, #6366f1)', icon: '✨', label: 'Dark Skies', type: 'forecast' },
  lenticular: { color: 'var(--color-lenticular, #ec4899)', icon: '🏔️', label: 'Lenticular', type: 'forecast' },
  clouds: { color: 'var(--color-clouds, #38bdf8)', icon: '☁️', label: 'Cloud Cover', type: 'forecast' },
  winds: { color: 'var(--color-winds, #10b981)', icon: '💨', label: 'Surface Winds', type: 'forecast' },
  hrtemp: { color: 'var(--color-hrtemp, #e11d48)', icon: '🌡️', label: 'Temperature Forecast', type: 'forecast' },
  hrtemp_current: { color: 'var(--color-hrtemp, #e11d48)', icon: '🌡️', label: 'Current Temperature (RTMA)', type: 'observation' },
  hrtemp_contours: { color: '#06b6d4', icon: '〰️', label: 'Temperature Contours', type: 'forecast' },

  lightning: { color: 'var(--color-lightning, #eab308)', icon: '⚡', label: 'Lightning Hazard', type: 'forecast' },
  smoke: { color: '#94a3b8', icon: '🌫️', label: 'Smoke & AQI', type: 'forecast' },
  dust: { color: '#d97706', icon: '🏜️', label: 'Global Dust', type: 'forecast' },
  dust_forecast: { color: '#d97706', icon: '🏜️', label: 'Global Dust', type: 'forecast' },
  aqi: { color: '#a855f7', icon: '🫁', label: 'Air Quality Index', type: 'forecast' },
  precip: { color: '#0284c7', icon: '🌧️', label: 'Precipitation Forecast', type: 'forecast' },
  precip_prob: { color: '#0284c7', icon: '🌧️', label: 'Precip Probability', type: 'forecast' },
  precip_24h: { color: '#0284c7', icon: '🌧️', label: 'Daily Precip', type: 'forecast' },
  snow_24h: { color: '#93c5fd', icon: '❄️', label: 'Daily Snow', type: 'forecast' },
  sun_intensity: { color: '#f59e0b', icon: '☀️', label: 'Sun Intensity', type: 'forecast' },
  valley_fog: { color: '#546e7a', icon: '🌫️', label: 'Low-Lying Fog', type: 'forecast' },
  cloud_inversion: { color: '#e0f2fe', icon: '🏔️', label: 'Cloud Inversion / Sea of Clouds', type: 'forecast' },
  cloud_base: { color: '#38bdf8', icon: '☁️', label: 'Cloud Base Elevation', type: 'forecast' },
  mountain_visibility: { color: '#64748b', icon: '🌫️', label: 'Mountain Visibility', type: 'forecast' },
  optics_dew_risk: { color: '#8b5cf6', icon: '🔭', label: 'Optics Dew Risk', type: 'forecast' },
  freezing_level: { color: '#3b82f6', icon: '🧊', label: 'Freezing Level', type: 'forecast' },
  freezing_level_contours: { color: '#06b6d4', icon: '〰️', label: 'Freezing Level Contours', type: 'forecast' },
  relative_humidity: { color: '#06b6d4', icon: '💧', label: 'Relative Humidity', type: 'forecast' },
  dewpoint: { color: '#38bdf8', icon: '🌫️', label: 'Dew Point', type: 'forecast' },
  windchill: { color: '#60a5fa', icon: '🥶', label: 'Wind Chill Index', type: 'forecast' },
  cape: { color: '#f59e0b', icon: '⚡', label: 'Convective Energy (CAPE)', type: 'forecast' },
  thunderstorm: { color: 'var(--color-thunderstorm, #eab308)', icon: '⚡', label: 'Thunderstorm Prob', type: 'forecast' },
  seeing: { color: '#818cf8', icon: '🔭', label: 'Astronomical Seeing', type: 'forecast' },
  turbulence: { color: '#f43f5e', icon: '✈️', label: 'Aviation Turbulence', type: 'forecast' },
  jet_stream: { color: '#38bdf8', icon: '🌀', label: 'Jet Stream & Winds Aloft', type: 'forecast' },
  sst: { color: '#06b6d4', icon: '🌊', label: 'Sea Surface Temp (SST)', type: 'observation' }
};


export class ManifestTimelineHelper {
  public static getStepDate(step: { date: string; hour: string; raw?: string }): Date {
    if (step.raw) {
      const d = new Date(step.raw);
      if (!isNaN(d.getTime())) return d;
    }
    let hourPart = cleanHour(step.hour);
    if (hourPart === 'sunrise' || hourPart === 'alpenglow_morning' || hourPart === 'goldenhour_morning') {
      return new Date(`${step.date}T13:00:00Z`);
    } else if (hourPart === 'sunset' || hourPart === 'alpenglow_evening' || hourPart === 'goldenhour_evening') {
      const baseD = new Date(`${step.date}T00:00:00Z`);
      return new Date(baseD.getTime() + 26 * 3600 * 1000);
    }

    if (!hourPart.includes(':')) {
      hourPart = `${hourPart.padStart(2, '0')}:00`;
    } else {
      const [h, m] = hourPart.split(':');
      hourPart = `${h.padStart(2, '0')}:${(m || '00').padStart(2, '0')}`;
    }
    return new Date(`${step.date}T${hourPart}:00Z`);
  }

  public static getAvailableModelsForProduct(manifest: Manifest | null, product: string): Array<{ id: string; label: string }> {
    if (!manifest) return [];

    const modelLabels: Record<string, string> = {
      gw_v2_blend_50: 'SkySkout HRes',
      gw_v2_hrrr: 'GW v2 HRRR (High-Res 3km)',
      gw_v2_rrfs: 'GW v2 RRFS (High-Res 3km)',
      gw_v2_rdps: 'GW v2 RDPS (Regional 10km Canada)',
      gw_v2_gfs: 'GW v2 GFS (Global 28km)',
      gw_v2_ecmwf: 'GW v2 ECMWF',
      gw_nowcast: 'SkySkout Sunset NowCast',
      blend_50: 'SkySkout HRes',
      blend_v2: 'Blend v2 Grid',
      alpenglow_blend: 'Alpenglow Blend',
      alpenglow_blend_v2: 'Alpenglow Blend v2',
      blend: 'SkySkout Weighted',
      ecmwf: 'ECMWF',
      gfs: 'GFS Global Forecast',
      hrrr: 'HRRR 3km High-Res',
      rdps: 'RDPS Canadian Regional',
      rrfs: 'RRFS Rapid Refresh',
      raqdps: 'RAQDPS Air Quality',
      rtma: 'RTMA Analysis/Obs',
      bortle: 'Bortle Light Pollution',
      ensemble: 'Ensemble Index',
      qpe_24h: 'MRMS QPE 24h Rain',
      mrms_snow_24h: 'MRMS 24h Snow Accum',
      mrms_snow_48h: 'MRMS 48h Snow Accum',
      goes_west: '🛰️ GOES-West Satellite (Pacific)',
      goes_east: '🛰️ GOES-East Satellite (Atlantic/CONUS)',
      nbm: 'NOAA NBM (National Blend 2.5km)',
      gdps: 'GDPS Canadian Global',
      icon13: 'ICON (Global 13km DWD)',
      hgefs: 'NOAA HGEFS AI Ensemble (28km)',
      dafs: 'NOAA DAFS GTG (3km CONUS)',
      noaa_crw_5km: '🛰️ NOAA 5km Satellite (CRW)'
    };

    if (manifest.models_config) {
      Object.keys(manifest.models_config).forEach(k => {
        const cfg = manifest.models_config![k];
        if (cfg && cfg.ui_label) {
          modelLabels[k] = cfg.ui_label;
        }
      });
    }
    modelLabels['gw_v2_blend_50'] = 'SkySkout HRes';
    modelLabels['blend_50'] = 'SkySkout HRes';

    let modelIds: string[] = [];
    let targetProduct = product;

    // Do not show model selectors for live observed weather products
    if (
      product === 'precip_radar' || 
      product === 'doppler_radar' || 
      product === 'rain_accum' || 
      product === 'precip_accum' || 
      product === 'snow_accum' || 
      product === 'lightning_live' || 
      product === 'lightning_realtime' || 
      product === 'usgs_streamflow' || 
      product === 'streamflow_gauges' || 
      product === 'streamflow' ||
      product === 'wildfires' ||
      product === 'wildfire_perimeters' ||
      product === 'fire_restrictions' ||
      product === 'avalanche' ||
      product === 'avalanche_danger'
    ) {
      return [];
    }

    if (product === 'precip_24h') {
      targetProduct = 'precip_24h';
    } else if (product === 'snow_24h') {
      targetProduct = 'snow_24h';
    } else if (product === 'precip_prob') {
      targetProduct = 'precip_prob';
    } else if (product.startsWith('precip')) {
      targetProduct = 'precip';
    } else if (product.startsWith('smoke')) {
      targetProduct = 'smoke';
    } else if (product.startsWith('dust')) {
      targetProduct = 'dust';
    } else if (product.startsWith('aqi') || product === 'aqhi' || product === 'aqi_index' || product === 'aqi_stations') {
      targetProduct = 'aqi';
    } else if (product === 'cloud_inversion' || product.startsWith('cloud_inversion')) {
      targetProduct = 'cloud_inversion';
    } else if (product === 'cloud_base' || product.startsWith('cloud_base')) {
      targetProduct = 'cloud_base';
    } else if (product === 'mountain_visibility' || product.startsWith('mountain_visibility')) {
      targetProduct = 'mountain_visibility';
    } else if (product.startsWith('cloud')) {
      targetProduct = 'clouds';
    } else if (product.startsWith('lenticular')) {
      targetProduct = 'lenticular';
    } else if (product === 'goldenhour' || product.startsWith('goldenhour')) {
      targetProduct = 'goldenhour';
    } else if (product.startsWith('golden_hour') || product.startsWith('sunrise')) {
      targetProduct = 'sunrise';
    } else if (product.startsWith('alpenglow')) {
      targetProduct = 'alpenglow';
    } else if (product.startsWith('darkskies') || product.startsWith('dark_skies')) {
      targetProduct = 'darkskies';
    } else if (product === 'hrtemp_contours') {
      targetProduct = 'hrtemp_contours';
    } else if (product === 'hrtemp_current') {
      targetProduct = 'hrtemp_current';
    } else if (product.startsWith('hrtemp') || product.startsWith('temp') || product.startsWith('surface_temp')) {
      targetProduct = 'hrtemp';
    } else if (product.startsWith('turbulence') || product === 'aviation_turbulence') {
      targetProduct = 'turbulence';
    } else if (product.startsWith('jet_stream')) {
      targetProduct = 'jet_stream';
    } else if (product.startsWith('windchill')) {
      targetProduct = 'windchill';
    } else if (product.startsWith('wind')) {
      targetProduct = 'winds';
    } else if (product.startsWith('lightning')) {
      targetProduct = 'lightning';
    } else if (product.startsWith('thunderstorm') || product === 'cape') {
      targetProduct = 'thunderstorm';
    } else if (product.startsWith('sun_intensity')) {
      targetProduct = 'sun_intensity';
    } else if (product.startsWith('seeing')) {
      targetProduct = 'seeing';
    } else if (product.startsWith('optics_dew_risk') || product.startsWith('dew_risk')) {
      targetProduct = 'optics_dew_risk';
    }

    if (manifest.exposed_models && manifest.exposed_models[targetProduct]) {
      modelIds = manifest.exposed_models[targetProduct];
    } else if ((manifest as any)[targetProduct]?.available_by_model) {
      const avail = (manifest as any)[targetProduct].available_by_model;
      if (Array.isArray(avail)) {
        const models = new Set<string>();
        avail.forEach((k: string) => {
          const m = k.match(/^([a-zA-Z0-9_]+?)_\d{4}-\d{2}-\d{2}_/);
          if (m) {
            models.add(m[1]);
          }
        });
        if (models.size > 0) {
          modelIds = Array.from(models);
        }
      } else if (typeof avail === 'object') {
        modelIds = Object.keys(avail);
      }
    } else if ((manifest as any)[targetProduct]?.available) {
      modelIds = Object.keys((manifest as any)[targetProduct].available);
    } else if (targetProduct === 'sunrise' || targetProduct === 'alpenglow' || targetProduct === 'goldenhour') {
      modelIds = ['gw_v2_blend_50', 'gw_v2_hrrr', 'gw_v2_rrfs', 'gw_v2_rdps', 'gw_v2_gfs', 'gw_v2_ecmwf', 'gw_nowcast'];
    } else if (targetProduct === 'darkskies') {
      modelIds = manifest.exposed_models?.['darkskies'] || ['blend', 'hrrr', 'rrfs', 'rdps', 'gfs', 'ecmwf', 'nbm', 'gdps', 'icon13'];
    } else if (targetProduct === 'valley_fog') {
      modelIds = manifest.exposed_models?.['valley_fog'] || ['hrrr', 'rrfs', 'rdps'];
    } else if (targetProduct === 'cloud_inversion') {
      modelIds = manifest.exposed_models?.['cloud_inversion'] || ['nbm'];
    } else if (targetProduct === 'cloud_base') {
      modelIds = manifest.exposed_models?.['cloud_base'] || ['nbm'];
    } else if (targetProduct === 'mountain_visibility') {
      modelIds = manifest.exposed_models?.['mountain_visibility'] || ['nbm'];
    } else if (targetProduct === 'relative_humidity' || targetProduct === 'dewpoint') {
      modelIds = manifest.exposed_models?.[targetProduct] || ['hrrr', 'rrfs', 'rdps', 'gfs', 'ecmwf', 'icon13', 'nbm'];
    } else if (targetProduct === 'windchill') {
      modelIds = manifest.exposed_models?.['windchill'] || ['hrrr', 'rrfs', 'rdps', 'gfs', 'ecmwf'];
    } else if (targetProduct === 'freezing_level' || targetProduct === 'freezing_level_contours') {
      modelIds = manifest.exposed_models?.['freezing_level'] || ['hrrr', 'rrfs', 'rdps', 'gfs', 'ecmwf'];
    } else if (targetProduct === 'hrtemp_contours') {
      modelIds = manifest.exposed_models?.['hrtemp_contours'] || ['skysk_hres_topo'];
    } else if (targetProduct === 'hrtemp_current') {
      modelIds = ['rtma'];
    } else if (targetProduct === 'turbulence') {
      modelIds = manifest.exposed_models?.['turbulence'] || ['dafs', 'gfs', 'ecmwf', 'hgefs', 'gdps', 'icon13'];
    } else if (targetProduct === 'jet_stream') {
      modelIds = manifest.exposed_models?.['jet_stream'] || ['hrrr', 'gfs', 'ecmwf', 'hgefs', 'gdps', 'icon13'];
    } else if (targetProduct === 'optics_dew_risk') {
      modelIds = manifest.exposed_models?.['optics_dew_risk'] || ['rdps', 'rrfs', 'gfs', 'ecmwf'];
    } else if (targetProduct === 'dust' || targetProduct === 'dust_forecast') {
      return [{ id: 'gefs_dust', label: 'GEFS Aerosols (Global Dust)' }];
    }

    if (targetProduct === 'sunrise' || targetProduct === 'alpenglow' || targetProduct === 'goldenhour') {
      modelIds = modelIds.map(m => (m === 'gw_v2_blend' || m === 'blend' || m === 'blend_50') ? 'gw_v2_blend_50' : m);
      if (!modelIds.includes('gw_v2_blend_50')) {
        modelIds.unshift('gw_v2_blend_50');
      }
      modelIds = Array.from(new Set(modelIds));
    }

    if (targetProduct === 'hrtemp' || product.startsWith('hrtemp') || product.startsWith('temp')) {
      modelIds = modelIds.filter(id => id !== 'rtma');
    }

    if (modelIds.length === 0) return [];

    return modelIds.map(id => {
      let label = modelLabels[id] || id.toUpperCase();
      if (targetProduct === 'darkskies' && (id === 'blend' || id === 'darkskies_blend')) {
        label = 'SkySkout Weighted';
      }
      return { id, label };
    });
  }

  /**
   * Generates the Master Timeline range.
   * Past window (-8h to 0h): 20-minute steps (24 steps: -480m to -20m).
   * 5-day horizon (0h to +120h): 1-hour steps (121 steps).
   * 10-day horizon (+123h to +240h): 3-hour steps (40 steps).
   */
  public static generateMasterTimelineRange(anchorTime?: Date, maxHours: number = 240): TimeStep[] {
    const range: TimeStep[] = [];
    const now = anchorTime || new Date();
    
    // Anchor to top of current UTC hour
    const nowRounded = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      0, 0, 0
    ));

    // Past 8 hours: 20-minute intervals (-480 min to -20 min = 24 steps)
    const pastStartMs = nowRounded.getTime() - 8 * 3600 * 1000;
    for (let i = 0; i < 24; i++) {
      const ms = pastStartMs + i * 20 * 60 * 1000;
      const d = new Date(ms);
      const dateStr = d.toISOString().split('T')[0];
      const hourStr = String(d.getUTCHours()).padStart(2, '0');
      const minStr = String(d.getUTCMinutes()).padStart(2, '0');
      range.push({
        date: dateStr,
        hour: `Z${hourStr}:${minStr}`,
        raw: `${dateStr} ${hourStr}:${minStr} UTC`
      });
    }

    // Future hours:
    // In 5-day mode (maxHours <= 120): 1-hour steps from 0h to 120h
    // In 10-day mode (maxHours > 120): 1-hour steps from 0h to 120h, then 3-hour steps from 123h to 240h
    const limitHours = Math.max(120, maxHours);
    for (let i = 0; i <= Math.min(120, limitHours); i++) {
      const ms = nowRounded.getTime() + i * 3600 * 1000;
      const d = new Date(ms);
      const dateStr = d.toISOString().split('T')[0];
      const hourStr = String(d.getUTCHours()).padStart(2, '0');
      range.push({
        date: dateStr,
        hour: `Z${hourStr}`,
        raw: `${dateStr}_Z${hourStr}`
      });
    }

    if (limitHours > 120) {
      // Extended zone (Days 6 to 10): 3-hour steps to match GFS / ECMWF frame cadence
      for (let i = 123; i <= limitHours; i += 3) {
        const ms = nowRounded.getTime() + i * 3600 * 1000;
        const d = new Date(ms);
        const dateStr = d.toISOString().split('T')[0];
        const hourStr = String(d.getUTCHours()).padStart(2, '0');
        range.push({
          date: dateStr,
          hour: `Z${hourStr}`,
          raw: `${dateStr}_Z${hourStr}`
        });
      }
    }

    return range;
  }

  /**
   * Checks if the forecast system / master timeline supports extended (10-day / 240h) timeline view.
   * Remains active during standard forecast operations, and hides when in historical webcam mode or climate ephemeris mode.
   */
  public static hasExtendedForecast(state: any): boolean {
    if (!state) return false;
    if (state.webcamsEnabled || state.climateSavedTimelineRange !== null || state.climateEnabled) {
      return false;
    }
    return true;
  }

  /**
   * Returns index of the "NOW" step (the first step at or after 0h without minute component).
   */
  public static getNowIndex(range: TimeStep[]): number {
    if (!range || range.length === 0) return 0;
    const idx = range.findIndex(s => !s.hour.includes(':'));
    return idx >= 0 ? idx : 0;
  }

  /**
   * Resolves the anchor Date for the "NOW" step on the master timeline.
   */
  public static getNowDate(range?: TimeStep[]): Date {
    if (range && range.length > 0) {
      const nowIdx = ManifestTimelineHelper.getNowIndex(range);
      const step = range[nowIdx];
      if (step) {
        let hourPart = step.hour.replace('Z', '').replace('UTC', '');
        if (!hourPart.includes(':')) hourPart = `${hourPart.padStart(2, '0')}:00`;
        const d = new Date(`${step.date}T${hourPart}:00Z`);
        if (!isNaN(d.getTime())) return d;
      }
    }
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0));
  }


  /**
   * Helper to normalize and match model keys, accounting for gw_v2_ prefixes and blend variants.
   */
  public static matchModelKey(keyModel: string, targetModel: string, isBlend: boolean = false): boolean {
    if (isBlend) {
      return (
        keyModel === 'gw_v2_blend_50' ||
        keyModel === 'gw_v2_blend' ||
        keyModel === 'blend_50' ||
        keyModel === 'blend' ||
        keyModel === 'alpenglow_blend' ||
        keyModel === 'goldenhour_blend' ||
        keyModel.includes('blend')
      );
    }
    if (keyModel === targetModel) return true;
    const cleanKey = keyModel.replace(/^gw_v2_/, '').toLowerCase();
    const cleanTarget = targetModel.replace(/^gw_v2_/, '').toLowerCase();
    return cleanKey === cleanTarget;
  }

  private static frameCache = new WeakMap<Manifest, Map<string, FrameMeta[]>>();

  /**
   * Extracts available frame timestamps for a specific product and model from manifest.
   */
  public static getProductFrames(manifest: Manifest, rawProduct: string, model: string): FrameMeta[] {
    const product = rawProduct === 'aviation_turbulence' ? 'turbulence' : rawProduct;
    const frames: FrameMeta[] = [];
    if (!manifest) return frames;

    const cacheKey = `${product}::${model || ''}`;
    if (product !== 'live_weather') {
      const manifestMap = this.frameCache.get(manifest);
      const cached = manifestMap?.get(cacheKey);
      if (cached) return cached;
    }

    try {
      if (product === 'goldenhour' || (product === 'alpenglow' && model && model.includes('goldenhour'))) {
        const keys = ((manifest as any).goldenhour?.available_by_model as string[])
          || ((manifest as any).goldenhour?.available_keys as string[])
          || [];
        const isBlend = !model || model === 'blend' || model === 'gw_v2_blend_50' || model === 'gw_v2_blend' || model === 'goldenhour_blend';
        const targetModel = isBlend ? 'gw_v2_blend_50' : model;
        keys.forEach(key => {
          const match = key.match(/^(.*)_(20\d{2}-\d{2}-\d{2})_(goldenhour_morning|goldenhour_evening)$/);
          if (match) {
            const keyModel = match[1];
            const dateStr = match[2];
            const event = match[3];
            const modelMatches = ManifestTimelineHelper.matchModelKey(keyModel, targetModel, isBlend);
            if (modelMatches) {
              const isMorning = (event === 'goldenhour_morning');
              let ts: number;
              if (isMorning) {
                ts = Date.parse(`${dateStr}T13:00:00Z`);
              } else {
                const baseD = new Date(`${dateStr}T00:00:00Z`);
                ts = baseD.getTime() + 26 * 3600 * 1000;
              }
              if (!isNaN(ts)) {
                frames.push({ dateStr, hourStr: event, timestamp: ts, key });
              }
            }
          }
        });
      } else if (product === 'alpenglow') {
        const keys = ((manifest as any).alpenglow?.available_by_model as string[]) 
          || ((manifest as any).alpenglow?.available_keys as string[]) 
          || [];
        const isBlend = !model || model === 'blend' || model === 'gw_v2_blend_50' || model === 'gw_v2_blend' || model === 'alpenglow_blend';
        const targetModel = isBlend ? 'gw_v2_blend_50' : model;
        keys.forEach(key => {
          const match = key.match(/^(.*)_(20\d{2}-\d{2}-\d{2})_(alpenglow_morning|alpenglow_evening)$/);
          if (match) {
            const keyModel = match[1];
            const dateStr = match[2];
            const event = match[3];
            const modelMatches = ManifestTimelineHelper.matchModelKey(keyModel, targetModel, isBlend);
            if (modelMatches) {
              const isMorning = (event === 'alpenglow_morning');
              let ts: number;
              if (isMorning) {
                ts = Date.parse(`${dateStr}T13:00:00Z`);
              } else {
                const baseD = new Date(`${dateStr}T00:00:00Z`);
                ts = baseD.getTime() + 26 * 3600 * 1000;
              }
              if (!isNaN(ts)) {
                frames.push({ dateStr, hourStr: event, timestamp: ts, key });
              }
            }
          }
        });
      } else if (product === 'sunrise') {
        const keys = ((manifest as any).sunrise?.available_by_model as string[])
          || (manifest.sunrise as any)?.available_keys || [];
        const isBlend = !model || model === 'blend' || model === 'gw_v2_blend_50' || model === 'gw_v2_blend';
        const targetModel = isBlend ? 'gw_v2_blend_50' : model;
        keys.forEach(key => {
          const match = key.match(/^(.*)_(20\d{2}-\d{2}-\d{2})_(sunrise|sunset)$/);
          if (match) {
            const keyModel = match[1];
            const dateStr = match[2];
            const event = match[3];
            const modelMatches = ManifestTimelineHelper.matchModelKey(keyModel, targetModel, isBlend);
            if (modelMatches) {
              const isMorning = (event === 'sunrise');
              let ts: number;
              if (isMorning) {
                ts = Date.parse(`${dateStr}T13:00:00Z`);
              } else {
                const baseD = new Date(`${dateStr}T00:00:00Z`);
                ts = baseD.getTime() + 26 * 3600 * 1000;
              }
              if (!isNaN(ts)) {
                frames.push({ dateStr, hourStr: event, timestamp: ts, key });
              }
            }
          }
        });
      } else if (product === 'aurora') {
        const keys = ((manifest as any).aurora?.available_by_model as string[]) || (manifest as any).aurora?.available_keys || [];
        keys.forEach((k: string) => {
          const match = k.match(/(20\d{2}-\d{2}-\d{2})/);
          if (match) {
            const dateStr = match[1];
            const ts = Date.parse(`${dateStr}T22:00:00Z`);
            if (!isNaN(ts)) {
              frames.push({ dateStr, hourStr: 'Z22', timestamp: ts, key: k });
            }
          }
        });
      } else if (product === 'darkskies') {
        const darkskiesAbm = manifest.darkskies?.available_by_model || (manifest.darkskies as any)?.available || {};
        const cleanModel = (model || '').replace(/^gw_v2_/, '').toLowerCase();
        let dates = (cleanModel && cleanModel !== 'bortle') ? (darkskiesAbm[model || ''] || darkskiesAbm[cleanModel]) : null;
        if (!dates) {
          dates = darkskiesAbm['blend'];
        }
        if (!dates && Object.keys(darkskiesAbm).length > 0) {
          dates = darkskiesAbm[Object.keys(darkskiesAbm)[0]];
        }
        if (dates && typeof dates === 'object') {
          Object.keys(dates).forEach(dateStr => {
            if (Array.isArray(dates[dateStr])) {
              dates[dateStr].forEach((h: string) => {
                const cleanH = h.replace(/^Z/i, '').replace(/UTC$/i, '').padStart(2, '0');
                const ts = Date.parse(`${dateStr}T${cleanH}:00:00Z`);
                if (!isNaN(ts)) {
                  frames.push({ dateStr, hourStr: `Z${cleanH}`, timestamp: ts, raw: `${dateStr}_Z${cleanH}` });
                }
              });
            }
          });
        }
      } else if (product === 'live_weather') {
        const bounds = (manifest as any).timeline_bounds?.live_weather;
        const now = Date.now();
        const minMs = bounds?.min_time ? new Date(bounds.min_time).getTime() : (now - 8 * 3600 * 1000);
        const maxMs = bounds?.max_time ? new Date(bounds.max_time).getTime() : now;
        let curr = minMs;
        while (curr <= maxMs) {
          const d = new Date(curr);
          const dateStr = d.toISOString().split('T')[0];
          const hourStr = String(d.getUTCHours()).padStart(2, '0');
          const minStr = String(d.getUTCMinutes()).padStart(2, '0');
          frames.push({ dateStr, hourStr: `Z${hourStr}:${minStr}`, timestamp: curr });
          curr += 10 * 60 * 1000;
        }
      } else if (product === 'lenticular') {
        const effectiveModel = (model === 'blend' || !model) ? 'ensemble' : model;
        const lentAbm = manifest.lenticular?.available_by_model || (manifest.lenticular as any)?.available || {};
        const cleanModel = effectiveModel.replace(/^gw_v2_/, '').toLowerCase();
        let modelData = lentAbm[effectiveModel] || lentAbm[cleanModel] || lentAbm['ensemble'] || lentAbm['ecmwf'];
        if (!modelData && !model && Object.keys(lentAbm).length > 0) modelData = lentAbm[Object.keys(lentAbm)[0]];
        if (!modelData) modelData = {};
        const seen = new Set<number>();
        Object.values(modelData).forEach((modeData: any) => {
          if (modeData && typeof modeData === 'object') {
            Object.keys(modeData).forEach(dateStr => {
              if (Array.isArray(modeData[dateStr])) {
                modeData[dateStr].forEach((h: string) => {
                  const cleanH = h.replace(/^Z/i, '').replace(/UTC$/i, '').padStart(2, '0');
                  const ts = Date.parse(`${dateStr}T${cleanH}:00:00Z`);
                  if (!isNaN(ts) && !seen.has(ts)) {
                    seen.add(ts);
                    frames.push({ dateStr, hourStr: `Z${cleanH}`, timestamp: ts, raw: `${dateStr}_Z${cleanH}` });
                  }
                });
              }
            });
          }
        });
      } else if (product === 'clouds') {
        const cloudsAbm = manifest.clouds?.available_by_model || (manifest.clouds as any)?.available || {};
        const cleanModel = (model || '').replace(/^gw_v2_/, '').toLowerCase();
        let modelData = cloudsAbm[model || ''] || cloudsAbm[cleanModel];
        if (!modelData && !model) {
          if (cloudsAbm['ecmwf']) modelData = cloudsAbm['ecmwf'];
          else if (cloudsAbm['gfs']) modelData = cloudsAbm['gfs'];
          else if (Object.keys(cloudsAbm).length > 0) modelData = cloudsAbm[Object.keys(cloudsAbm)[0]];
        }
        if (!modelData) modelData = {};
        const seen = new Set<number>();
        Object.values(modelData).forEach((levelData: any) => {
          if (levelData && Array.isArray(levelData)) {
            levelData.forEach((timeStr: string) => {
              const parts = timeStr.split('_');
              if (parts.length === 2) {
                const dateStr = parts[0];
                const cleanH = parts[1].replace(/^Z/i, '').replace(/UTC$/i, '').padStart(2, '0');
                const ts = Date.parse(`${dateStr}T${cleanH}:00:00Z`);
                if (!isNaN(ts) && !seen.has(ts)) {
                  seen.add(ts);
                  frames.push({ dateStr, hourStr: `Z${cleanH}`, timestamp: ts, raw: timeStr });
                }
              }
            });
          }
        });
      } else if (product === 'smoke' || product === 'dust' || product === 'dust_forecast') {
        const smokeAbm = manifest.smoke?.available_by_model || (manifest.smoke as any)?.available || {};
        const cleanModel = (model || (product.startsWith('dust') ? 'gefs_dust' : '')).replace(/^gw_v2_/, '').toLowerCase();
        let modelData = smokeAbm[model || ''] || smokeAbm[cleanModel];
        if (!modelData && !model) {
          if (product.startsWith('dust')) modelData = smokeAbm['gefs_dust'];
          else if (smokeAbm['blend']) modelData = smokeAbm['blend'];
          else if (smokeAbm['hrrr']) modelData = smokeAbm['hrrr'];
          else if (Object.keys(smokeAbm).length > 0) modelData = smokeAbm[Object.keys(smokeAbm)[0]];
        }
        if (!modelData) modelData = {};
        const seen = new Set<number>();
        Object.values(modelData).forEach((levelData: any) => {
          if (levelData && typeof levelData === 'object') {
            Object.keys(levelData).forEach(dateStr => {
              if (Array.isArray(levelData[dateStr])) {
                levelData[dateStr].forEach((h: string) => {
                  const cleanH = h.replace(/^Z/i, '').replace(/UTC$/i, '').padStart(2, '0');
                  const ts = Date.parse(`${dateStr}T${cleanH}:00:00Z`);
                  if (!isNaN(ts) && !seen.has(ts)) {
                    seen.add(ts);
                    frames.push({ dateStr, hourStr: `Z${cleanH}`, timestamp: ts, raw: `${dateStr}_Z${cleanH}` });
                  }
                });
              }
            });
          }
        });
      } else if (manifest[product as keyof Manifest] || product === 'hrtemp_current') {
        const prodKey = (product === 'hrtemp_current') ? 'hrtemp' : product;
        const prodObj = manifest[prodKey as keyof Manifest] as any;
        const abm = prodObj?.available_by_model || prodObj?.available || {};
        const effectiveModel = (product === 'hrtemp_current') ? 'rtma' : model;
        const cleanModel = (effectiveModel || '').replace(/^gw_v2_/, '').toLowerCase();
        const nameOnDisk = manifest?.models_config?.[model]?.name_on_disk;
        let dates = abm[effectiveModel || ''] || abm[cleanModel] || abm[`gw_v2_${cleanModel}`] || (nameOnDisk ? abm[nameOnDisk] : undefined);
        if (!dates && model) {
          const matchKey = Object.keys(abm).find(k => 
            k.toLowerCase() === model.toLowerCase() || 
            k.toLowerCase() === cleanModel ||
            (nameOnDisk && k.toLowerCase() === nameOnDisk.toLowerCase())
          );
          if (matchKey) {
            dates = abm[matchKey];
          }
        }
        const hasNonEmptyDates = (d: any) => !!(d && typeof d === 'object' && Object.keys(d).length > 0);
        if (!hasNonEmptyDates(dates) && Object.keys(abm).length > 0) {
          const exposedList: string[] = (manifest.exposed_models && manifest.exposed_models[prodKey]) || [];
          const preferredKey = exposedList.find(k => hasNonEmptyDates(abm[k]) || hasNonEmptyDates(abm[k.toLowerCase()]))
            || Object.keys(abm).find(k => hasNonEmptyDates(abm[k]));
          if (preferredKey) {
            dates = abm[preferredKey] || abm[preferredKey.toLowerCase()];
          }
        }
        if (dates && typeof dates === 'object') {
          Object.keys(dates).forEach(dateStr => {
            if (Array.isArray(dates[dateStr])) {
              if (dates[dateStr].length === 0) {
                // Daily-resolution products without individual hours
                const ts = Date.parse(`${dateStr}T00:00:00Z`);
                if (!isNaN(ts)) {
                  frames.push({ dateStr, hourStr: 'Z00', timestamp: ts, raw: `${dateStr}_Z00` });
                }
              } else {
                dates[dateStr].forEach((h: string) => {
                  const cleanH = h.replace(/^Z/i, '').replace(/UTC$/i, '').padStart(2, '0');
                  const ts = Date.parse(`${dateStr}T${cleanH}:00:00Z`);
                  if (!isNaN(ts)) {
                    frames.push({ dateStr, hourStr: `Z${cleanH}`, timestamp: ts, raw: `${dateStr}_Z${cleanH}` });
                  }
                });
              }
            }
          });
        }
      }

    } catch (err) {
      console.warn(`[ManifestTimelineHelper] Error extracting frames for product ${product}:`, err);
    }

    frames.sort((a, b) => a.timestamp - b.timestamp);
    if (product !== 'live_weather') {
      let manifestMap = this.frameCache.get(manifest);
      if (!manifestMap) {
        manifestMap = new Map<string, FrameMeta[]>();
        this.frameCache.set(manifest, manifestMap);
      }
      manifestMap.set(cacheKey, frames);
    }
    return frames;
  }

  /**
   * Computes product availability bounds (minTime, maxTime, sorted frames, metadata).
   */
  public static getProductBounds(manifest: Manifest, rawProduct: string, model: string): ProductBoundsInfo | null {
    const product = rawProduct === 'aviation_turbulence' ? 'turbulence' : rawProduct;
    const frames = this.getProductFrames(manifest, product, model);
    const meta = PRODUCT_META[product] || { color: '#38bdf8', icon: '🌤️', label: product, type: 'forecast' };

    if (frames.length > 0) {
      const minTime = new Date(frames[0].timestamp);
      const maxTime = new Date(frames[frames.length - 1].timestamp);

      return {
        product,
        minTime,
        maxTime,
        frames,
        color: meta.color,
        icon: meta.icon,
        label: meta.label,
        type: meta.type
      };
    }

    // Only fall back to prodObj.min_time/max_time for products that do NOT have
    // per-model availability catalogs (e.g. live observations, static layers, or unmodeled single products)
    const prodObj = manifest && (manifest as any)[product];
    const hasModelCatalog = Boolean(prodObj?.available_by_model || prodObj?.available);
    if (prodObj && !hasModelCatalog) {
      const minStr = prodObj.min_time || prodObj.minTime;
      const maxStr = prodObj.max_time || prodObj.maxTime;
      if (minStr && maxStr) {
        const minTime = new Date(minStr);
        const maxTime = new Date(maxStr);
        if (!isNaN(minTime.getTime()) && !isNaN(maxTime.getTime())) {
          return {
            product,
            minTime,
            maxTime,
            frames: [],
            color: meta.color,
            icon: meta.icon,
            label: meta.label,
            type: meta.type
          };
        }
      }
    }

    return null;
  }

  /**
   * Clamps targetTime to [minTime, maxTime] and finds the closest frame in available timestamps.
   */
  public static getClampedFrame(manifest: Manifest, product: string, model: string, targetTime: Date): FrameMeta | null {
    const frames = this.getProductFrames(manifest, product, model);
    if (frames.length === 0) return null;

    const targetMs = targetTime.getTime();
    if (targetMs <= frames[0].timestamp) {
      return frames[0];
    }
    if (targetMs >= frames[frames.length - 1].timestamp) {
      return frames[frames.length - 1];
    }

    let bestFrame = frames[0];
    let minDiff = Math.abs(frames[0].timestamp - targetMs);

    for (let i = 1; i < frames.length; i++) {
      const diff = Math.abs(frames[i].timestamp - targetMs);
      if (diff < minDiff) {
        minDiff = diff;
        bestFrame = frames[i];
      }
    }

    return bestFrame;
  }

  /**
   * Resolves the timestamp of the next upcoming twilight event for `gw_nowcast`
   * (or the latest available `gw_nowcast` frame in `manifest`) and snaps it to
   * the closest step in `timelineRange` if provided.
   */
  public static getNextNowcastEventDate(
    manifest: Manifest | null | undefined,
    product: string = 'sunrise',
    timelineRange?: { date: string; hour: string; timestamp?: number }[]
  ): Date | null {
    const queryProd = (product === 'alpenglow_forecast') ? 'alpenglow' : (product === 'golden_hour' ? 'goldenhour' : product);
    const nowMs = Date.now();
    let targetMs: number | null = null;

    if (manifest) {
      let frames = this.getProductFrames(manifest, queryProd, 'gw_nowcast');
      if (frames.length === 0 && queryProd !== 'sunrise') {
        frames = this.getProductFrames(manifest, 'sunrise', 'gw_nowcast');
      }
      if (frames.length > 0) {
        // Prefer the first frame whose timestamp is in the future (or within the last 3 hours of active twilight)
        const upcoming = frames.find(f => f.timestamp >= nowMs - 3 * 3600 * 1000);
        const chosen = upcoming || frames[frames.length - 1];
        targetMs = chosen.timestamp;
      }
    }

    if (targetMs === null) {
      // Fallback astronomical estimate: next North American sunrise (~13:00 UTC) or sunset (~02:00 UTC)
      const now = new Date(nowMs);
      const utcHour = now.getUTCHours() + now.getUTCMinutes() / 60;
      const baseMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0);
      if (utcHour < 15.5) {
        targetMs = baseMidnight + 13 * 3600 * 1000;
      } else {
        targetMs = baseMidnight + 26 * 3600 * 1000;
      }
    }

    if (Array.isArray(timelineRange) && timelineRange.length > 0) {
      let bestStepDate: Date | null = null;
      let bestDiff = Infinity;
      for (const step of timelineRange) {
        let stepMs = typeof step.timestamp === 'number' ? step.timestamp : NaN;
        if (isNaN(stepMs)) {
          const hMatch = String(step.hour || '').match(/^Z?(\d{1,2})$/i);
          if (hMatch && step.date) {
            stepMs = Date.parse(`${step.date}T${hMatch[1].padStart(2, '0')}:00:00Z`);
          } else if (step.hour === 'sunrise' || step.hour === 'goldenhour_morning' || step.hour === 'alpenglow_morning') {
            stepMs = Date.parse(`${step.date}T13:00:00Z`);
          } else if (step.hour === 'sunset' || step.hour === 'goldenhour_evening' || step.hour === 'alpenglow_evening') {
            stepMs = Date.parse(`${step.date}T00:00:00Z`) + 26 * 3600 * 1000;
          }
        }
        if (!isNaN(stepMs)) {
          const diff = Math.abs(stepMs - targetMs);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestStepDate = new Date(stepMs);
          }
        }
      }
      if (bestStepDate) {
        return bestStepDate;
      }
    }

    return new Date(targetMs);
  }

  /**
   * Resolves the two bracketing frames (t0 <= targetTime <= t1) and a normalized
   * linear blend factor [0..1] for temporal interpolation across 1h/3h/6h steps.
   * If the gap between frames exceeds maxGapHours, snaps to the nearest frame (blend = 0).
   */
  public static getBracketingFrames(
    manifest: Manifest,
    product: string,
    model: string,
    targetTime: Date | number,
    maxGapHours: number = 6
  ): BracketingFrames | null {
    const frames = this.getProductFrames(manifest, product, model);
    if (frames.length === 0) return null;

    const targetMs = typeof targetTime === 'number' ? targetTime : targetTime.getTime();
    if (targetMs <= frames[0].timestamp) {
      return { frame0: frames[0], frame1: frames[0], blend: 0, intervalHours: 0 };
    }
    const lastIdx = frames.length - 1;
    if (targetMs >= frames[lastIdx].timestamp) {
      return { frame0: frames[lastIdx], frame1: frames[lastIdx], blend: 0, intervalHours: 0 };
    }

    // Binary search for largest index lo where frames[lo].timestamp <= targetMs
    let lo = 0;
    let hi = lastIdx;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      if (frames[mid].timestamp <= targetMs) {
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    const i0 = Math.max(0, lo - 1);
    const i1 = Math.min(lastIdx, i0 + 1);
    const f0 = frames[i0];
    const f1 = frames[i1];

    if (f0.timestamp === f1.timestamp) {
      return { frame0: f0, frame1: f0, blend: 0, intervalHours: 0 };
    }

    const spanMs = f1.timestamp - f0.timestamp;
    const intervalHours = spanMs / (3600 * 1000);
    if (intervalHours > maxGapHours) {
      const nearest = (targetMs - f0.timestamp) <= (f1.timestamp - targetMs) ? f0 : f1;
      return { frame0: nearest, frame1: nearest, blend: 0, intervalHours };
    }

    const rawBlend = (targetMs - f0.timestamp) / spanMs;
    const blend = Math.max(0, Math.min(1, rawBlend));
    return { frame0: f0, frame1: f1, blend, intervalHours };
  }
}
export default ManifestTimelineHelper;
